/**
 * Accept Invitation Page
 *
 * A public page where users can accept invitations sent by admins.
 * Users click a link from their email with a token, validate the invitation,
 * log in with Azure AD, and accept to create their account with pre-assigned roles.
 */

import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../lib/msalConfig';
import { apiRequest } from '../lib/api';

interface ValidationResponse {
  valid: boolean;
  invitation?: {
    email: string;
    displayName?: string;
    expiresAt: string;
  };
  reason?: string;
}

export default function AcceptInvitationPage() {
  const { instance, accounts } = useMsal();
  const [token] = useState<string | null>(() => {
    // First check URL params (initial visit)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      // Store token for retrieval after Azure AD redirect
      sessionStorage.setItem('invitation_token', urlToken);
      return urlToken;
    }
    // After Azure AD redirect, retrieve from sessionStorage
    return sessionStorage.getItem('invitation_token');
  });
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setValidation({ valid: false, reason: 'No invitation token provided' });
      setLoading(false);
      return;
    }

    // Basic token format validation (URL-safe base64 should be ~43 chars)
    if (token.length < 20) {
      setValidation({ valid: false, reason: 'Invalid token format' });
      setLoading(false);
      return;
    }

    // Validate invitation (public endpoint, no auth needed)
    const validateInvitation = async () => {
      try {
        const response = await apiRequest('GET', `/api/invitations/validate?token=${encodeURIComponent(token)}`);
        const data = await response.json();
        setValidation(data);
      } catch (err) {
        setValidation({ valid: false, reason: 'Failed to validate invitation' });
      } finally {
        setLoading(false);
      }
    };
    validateInvitation();
  }, [token]);

  const handleLogin = async () => {
    try {
      // Use fixed redirect URI - token is stored in sessionStorage
      // Force re-authentication to prevent using cached sessions from other users
      await instance.loginRedirect({
        ...loginRequest,
        redirectUri: `${window.location.origin}/invite`,
        prompt: "login",
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to initiate login' });
    }
  };

  const handleLogout = async () => {
    try {
      await instance.logoutRedirect();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to log out' });
    }
  };

  const handleAccept = async () => {
    if (!accounts[0]) {
      setMessage({ type: 'error', text: 'Please log in first' });
      return;
    }

    try {
      setAccepting(true);
      const response = await apiRequest('POST', '/api/invitations/accept', { token });

      if (response.ok) {
        // Clear the stored token after successful acceptance
        sessionStorage.removeItem('invitation_token');
        setMessage({ type: 'success', text: 'Invitation accepted! Redirecting...' });
        setTimeout(() => {
          window.location.href = '/fields/mappings';
        }, 2000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to accept invitation' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to accept invitation' });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!validation?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h1 className="text-2xl font-bold text-gray-900 mt-4">Invalid Invitation</h1>
            <p className="text-gray-600 mt-2">{validation?.reason || 'This invitation is not valid.'}</p>
            <a
              href="/welcome"
              className="mt-6 inline-block px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Go to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  const invitation = validation.invitation!;
  const isLoggedIn = accounts.length > 0;
  const emailMatches = isLoggedIn && accounts[0].username?.toLowerCase() === invitation.email.toLowerCase();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <div className="text-center mb-6">
          <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900 mt-4">You're Invited!</h1>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-600">
            <strong>Email:</strong> {invitation.email}
          </p>
          {invitation.displayName && (
            <p className="text-sm text-gray-600 mt-1">
              <strong>Name:</strong> {invitation.displayName}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
          </p>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded-md text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {!isLoggedIn ? (
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Please log in to accept this invitation with your Peter Lang account.
            </p>
            <button
              onClick={handleLogin}
              className="w-full px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Log In to Accept
            </button>
          </div>
        ) : !emailMatches ? (
          <div className="text-center">
            <p className="text-red-600 mb-4">
              You're logged in as <strong>{accounts[0].username}</strong>, but this invitation is for <strong>{invitation.email}</strong>.
            </p>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-white bg-gray-600 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Log Out
            </button>
          </div>
        ) : (
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {accepting ? 'Accepting...' : 'Accept Invitation'}
          </button>
        )}
      </div>
    </div>
  );
}
